// frontend/src/Patients.js
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from './axiosConfig';
import UIIcon from './UIIcon';
import './Patients.css';

/**
 * Regex for Chinese/Japanese/Korean blocks.
 */
const CJK_REGEX =
  /[\u3400-\u4DBF\u4E00-\u9FFF\uF900-\uFAFF\u3040-\u309F\u30A0-\u30FF\u3100-\u312F\u31A0-\u31BF\u3000-\u303F\uFF00-\uFFEF]|\u{20000}-\u{2EBEF}/u;

// how often to refresh unread report/message counts (ms)
const UNREAD_POLL_MS = 60_000;

/**
 * Same as in PatientMessages.js:
 * latest activity across root message + all replies.
 */
function computeLatestActivityTs(messages) {
  let max = 0;
  (messages || []).forEach((m) => {
    const rootTs = new Date(m.created_at || 0).getTime();
    if (rootTs > max) max = rootTs;

    if (!Array.isArray(m.replies)) return;
    m.replies.forEach((r) => {
      const t = new Date(r.created_at || 0).getTime();
      if (t > max) max = t;
    });
  });
  return max;
}

/**
 * Inline SVG so Linux never needs an emoji font to render an envelope.
 * Uses currentColor so it matches your badge color.
 */
function EnvelopeIcon({ size = 14, title = 'New messages' }) {
  return (
    <span
      aria-label={title}
      role="img"
      title={title}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        lineHeight: 0,
        verticalAlign: 'middle',
      }}
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M4 6.75C4 5.784 4.784 5 5.75 5h12.5C19.216 5 20 5.784 20 6.75v10.5c0 .966-.784 1.75-1.75 1.75H5.75C4.784 19 4 18.216 4 17.25V6.75Z"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinejoin="round"
        />
        <path
          d="M5.5 7.25 12 12.25l6.5-5"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}

const Patients = () => {
  const { t } = useTranslation();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterText, setFilterText] = useState('');
  const [cjkWarning, setCjkWarning] = useState('');
  const [unreadMap, setUnreadMap] = useState({}); // { [patientId]: reportsUnreadCount }
  const [msgUnreadMap, setMsgUnreadMap] = useState({}); // { [patientId]: hasNewMessages }
  const pollRef = useRef(null);
  const composingRef = useRef(false);

  const navigate = useNavigate();

  const userRole = localStorage.getItem('userRole') || '';

  const allowedEditRoles = ['admin', 'supervisor', 'doctor', 'staff'];
  const allowedDeleteRoles = ['admin', 'supervisor', 'doctor'];

  // --- load patients
  const fetchPatients = useCallback(async () => {
    try {
      const res = await axios.get('patients/');
      setPatients(res.data || []);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(t('error.fetchPatients'), err);
      setError(t('error.failedLoadPatients'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  // --- load unread report counts for a list of patients
  const fetchUnreadForPatients = useCallback(async (list) => {
    if (!Array.isArray(list) || list.length === 0) return;

    // add ?scan=1 so backend scans Azure & upserts new PatientReport rows
    const requests = list.map((p) =>
      axios
        .get(`patients/${p.id}/reports/summary/`)
        .then((r) => ({ id: p.id, unread: r.data?.unread ?? 0 }))
        .catch(() => ({ id: p.id, unread: 0 }))
    );

    const results = await Promise.all(requests);
    setUnreadMap((prev) => {
      const next = { ...prev };
      results.forEach(({ id, unread }) => {
        next[id] = unread;
      });
      return next;
    });
  }, []);

  // --- load "new message" flags for a list of patients
  const fetchMsgUnreadForPatients = useCallback(async (list) => {
    if (!Array.isArray(list) || list.length === 0) return;

    const requests = list.map((p) =>
      axios
        .get('patient-messages/', { params: { patient: p.id } })
        .then((r) => ({ id: p.id, messages: r.data || [] }))
        .catch(() => ({ id: p.id, messages: [] }))
    );

    const results = await Promise.all(requests);

    setMsgUnreadMap((prev) => {
      const next = { ...prev };
      results.forEach(({ id, messages }) => {
        const latest = computeLatestActivityTs(messages);
        if (!latest) {
          next[id] = false;
          return;
        }
        const key = `pm_last_seen_dept_reply_${id}`;
        let lastSeen = 0;
        try {
          lastSeen = Number(localStorage.getItem(key) || '0');
        } catch {
          lastSeen = 0;
        }
        next[id] = !lastSeen || latest > lastSeen;
      });
      return next;
    });
  }, []);

  // initial load
  useEffect(() => {
    (async () => {
      await fetchPatients();
    })();
  }, [fetchPatients]);

  // when patients change, refresh unread counts immediately (reports + messages)
  useEffect(() => {
    if (patients.length > 0) {
      fetchUnreadForPatients(patients);
      fetchMsgUnreadForPatients(patients);
    } else {
      setUnreadMap({});
      setMsgUnreadMap({});
    }
  }, [patients, fetchUnreadForPatients, fetchMsgUnreadForPatients]);

  // polling for unread counts (reports + messages)
  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => {
      if (patients.length > 0) {
        fetchUnreadForPatients(patients);
        fetchMsgUnreadForPatients(patients);
      }
    }, UNREAD_POLL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [patients, fetchUnreadForPatients, fetchMsgUnreadForPatients]);

  // also refresh when tab becomes visible again
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible' && patients.length > 0) {
        fetchUnreadForPatients(patients);
        fetchMsgUnreadForPatients(patients);
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [patients, fetchUnreadForPatients, fetchMsgUnreadForPatients]);

  const handleEdit = (patientId) => navigate(`/dashboard/patients/edit/${patientId}`);
  const handleView = (patientId) => navigate(`/dashboard/patients/${patientId}/view`);
  const handleDelete = (patientId) => {
    if (window.confirm(t('confirm.deletePatient'))) {
      axios
        .delete(`patients/${patientId}/`)
        .then(() => fetchPatients())
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.error(t('error.deletePatient'), err);
          setError(t('error.failedDeletePatient'));
        });
    }
  };

  // --- Input handlers with IME-safe CJK validation ---
  const validateNoCJK = (val) => {
    if (!val) {
      setCjkWarning('');
      return;
    }
    if (CJK_REGEX.test(val)) {
      setCjkWarning(t('warn.noCJK', 'Chinese/CJK characters are not allowed in this field.'));
    } else {
      setCjkWarning('');
    }
  };

  const onCompositionStart = () => {
    composingRef.current = true;
  };
  const onCompositionEnd = (e) => {
    composingRef.current = false;
    const v = e.target.value || '';
    validateNoCJK(v);
  };
  const onFilterChange = (e) => {
    const v = e.target.value;
    setFilterText(v);
    if (!composingRef.current) {
      validateNoCJK(v);
    }
  };

  // --- Client-side filter so the input is useful ---
  const filteredPatients = useMemo(() => {
    const q = (filterText || '').trim().toLowerCase();
    if (!q) return patients;
    return patients.filter((p) => {
      const serviceCenterName =
        (p.service_center_detail && p.service_center_detail.name) ||
        (p.service_center && p.service_center.name) ||
        '';

      const parts = [
        p.first_name || '',
        p.last_name || '',
        p.distributors_mrn || '',
        p.doc_number || '',
        serviceCenterName,
      ].map((s) => (s || '').toLowerCase());
      return parts.some((s) => s.includes(q));
    });
  }, [patients, filterText]);

  const unreadReportTotal = Object.values(unreadMap).reduce((sum, value) => sum + Number(value || 0), 0);
  const patientsWithMessages = Object.values(msgUnreadMap).filter(Boolean).length;

  if (loading) {
    return (
      <div className="pt-container pt-loading" role="status">
        <div className="pt-skeleton pt-skeleton--title" />
        <div className="pt-skeleton pt-skeleton--toolbar" />
        <div className="pt-skeleton pt-skeleton--table" />
      </div>
    );
  }
  if (error) return <div className="pt-container pt-error">{error}</div>;

  return (
    <div className="pt-container">
      <section className="pt-hero">
        <div className="pt-hero-copy">
          <span className="pt-eyebrow">{t('patients.workspaceKicker')}</span>
          <h2>{t('patients')}</h2>
          <p>{t('patients.workspaceSubtitle')}</p>
        </div>
        <button className="pt-add" onClick={() => navigate('/dashboard/patients/new')}>
          <UIIcon name="plus" size={17} />
          {t('addNewPatient')}
        </button>
      </section>

      <section className="pt-overview" aria-label={t('patients.overviewAria')}>
        <div className="pt-stat"><span>{t('patients.workspaceTitle')}</span><strong>{patients.length}</strong><small>{t('patients.available')}</small></div>
        <div className="pt-stat"><span>{t('patients.visibleResults')}</span><strong>{filteredPatients.length}</strong><small>{filterText ? t('patients.matchingSearch') : t('patients.currentList')}</small></div>
        <div className="pt-stat"><span>{t('patients.reportsWaiting')}</span><strong>{unreadReportTotal}</strong><small>{t('patients.unreadDsiReports')}</small></div>
        <div className="pt-stat"><span>{t('patients.newMessagesMetric')}</span><strong>{patientsWithMessages}</strong><small>{t('patients.activityPatients')}</small></div>
      </section>

      <section className="pt-toolbar">
        <div className="pt-search-wrap">
          <UIIcon name="search" size={17} className="pt-search-icon" />
          <div className="pt-search-copy">
            <label htmlFor="patientFilter">{t('filter', 'Search patients')}</label>
            <input
              id="patientFilter"
              className={`pt-input ${cjkWarning ? 'pt-input-warning' : ''}`}
              type="text"
              placeholder={t('filterPlaceholder', 'Search name, MRN, document number, or clinic')}
              value={filterText}
              onChange={onFilterChange}
              onCompositionStart={onCompositionStart}
              onCompositionEnd={onCompositionEnd}
              aria-invalid={cjkWarning ? 'true' : 'false'}
              aria-describedby={cjkWarning ? 'cjkWarn' : undefined}
              inputMode="text"
              autoComplete="off"
            />
          </div>
          {filterText && (
            <button type="button" className="pt-clear-search" onClick={() => { setFilterText(''); setCjkWarning(''); }}>
              {t('patients.clear')}
            </button>
          )}
        </div>
        {cjkWarning && <div id="cjkWarn" className="pt-warning" aria-live="polite">{cjkWarning}</div>}
      </section>

      {filteredPatients.length > 0 ? (
        <div className="pt-table-wrap">
          <div className="pt-table-heading">
            <div><strong>{t('patients.directory')}</strong><span>{t('patients.records', { count: filteredPatients.length })}</span></div>
            <small>{t('patients.directoryHint')}</small>
          </div>
          <table className="pt-table">
            <thead>
              <tr>
                <th>{t('patients')}</th>
                <th>{t('distributorsMRN')}</th>
                <th>{t('serviceCenter')}</th>
                <th>{t('dsiDate')}</th>
                <th>{t('docNumber')}</th>
                <th>{t('fitDate')}</th>
                <th>{t('actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredPatients.map((p) => {
                const unreadReports = unreadMap[p.id] || 0;
                const hasNewMsg = !!msgUnreadMap[p.id];
                const serviceCenterName =
                  (p.service_center_detail && p.service_center_detail.name) ||
                  (p.service_center && p.service_center.name) ||
                  t('nA');
                const initials = `${p.first_name?.[0] || ''}${p.last_name?.[0] || ''}`.toUpperCase() || 'P';

                return (
                  <tr key={p.id} className="pt-row">
                    <td data-label={t('patients.table.patient')}>
                      <div className="pt-patient-cell">
                        <span className="pt-avatar">{initials}</span>
                        <div className="pt-patient-copy">
                          <strong>{p.first_name} {p.last_name}</strong>
                          <span>{t('patients.patientId', { id: p.id })}</span>
                        </div>
                        <div className="pt-alerts">
                          {p.dsi_recommendation_enabled && p.latest_recommendation_status && (
                            <span
                              className={`pt-rec-mark ${p.latest_recommendation_status === 'treatment_recommended' ? 'is-recommended' : 'is-not-recommended'}`}
                              title={p.latest_recommendation_status === 'treatment_recommended' ? t('recommendation.recommended') : t('recommendation.notRecommended')}
                              aria-label={p.latest_recommendation_status === 'treatment_recommended' ? t('recommendation.recommended') : t('recommendation.notRecommended')}
                            >
                              {p.latest_recommendation_status === 'treatment_recommended' ? t('dsi.recommendedMark') : t('dsi.notRecommendedMark')}
                            </span>
                          )}
                          {unreadReports > 0 && <span className="pt-badge-unread" title={t('patients.reportsAwaitingReview')}>{unreadReports}</span>}
                          {hasNewMsg && <span className="pt-badge-message" title={t('patients.newMessages')}><EnvelopeIcon size={13} title={t('patients.newMessages')} /></span>}
                        </div>
                      </div>
                    </td>
                    <td data-label={t('patients.table.mrn')}><span className="pt-mono">{p.distributors_mrn || t('nA')}</span></td>
                    <td data-label={t('patients.table.serviceCenter')}><span className="pt-clinic-pill">{serviceCenterName}</span></td>
                    <td data-label={t('patients.table.dsiDate')}>{p.dsi_date || t('nA')}</td>
                    <td data-label={t('patients.table.document')}>{p.doc_number || t('nA')}</td>
                    <td data-label={t('patients.table.fitDate')}>{p.fit_date || t('nA')}</td>
                    <td data-label={t('patients.table.actions')}>
                      <div className="pt-actions">
                        <button className="pt-btn pt-btn-primary" onClick={() => handleView(p.id)}>
                          {t('view')} <UIIcon name="chevronRight" size={14} />
                        </button>
                        {allowedEditRoles.includes(userRole) && <button className="pt-btn" onClick={() => handleEdit(p.id)}>{t('edit')}</button>}
                        {allowedDeleteRoles.includes(userRole) && <button className="pt-btn pt-btn-danger" onClick={() => handleDelete(p.id)}>{t('delete')}</button>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="pt-empty">
          <span className="pt-empty-icon"><UIIcon name="search" size={22} /></span>
          <strong>{t('noPatients')}</strong>
          <p>{t('patients.noMatchHint')}</p>
          {filterText && <button type="button" onClick={() => setFilterText('')}>{t('patients.clearSearch')}</button>}
        </div>
      )}
    </div>
  );
};

export default Patients;
